import { Button, Checkbox, Col, Divider, Input, message, Row, Select, Spin } from "antd";
import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DrawRequestsTableNew } from "./components/DrawRequestsTableNew/DrawRequestsTableNew";
import styles from "./DrawRequestNew.module.css";
import { IManipulateReportsFilters, IManipulateReportsOptions, ManipulateReports } from "../../components";
import moment, { isMoment } from "moment";
import { useNavigate } from "react-router";
import { DrawRequestApprovalType, DrawRequestResp } from "../../types/draw-request";
import { useQuery } from "@tanstack/react-query";
import { drawRequestsService } from "../../services/draw-request";
import { CloudDownloadOutlined, PlusOutlined } from "@ant-design/icons";
import { generateDownload } from "../../helpers";
import { AuthContext } from "../../contexts/AuthContext";
import { CheckboxProps } from "antd/es/checkbox";

interface Props {}

/**========================================================================
 *                           Const here
 *========================================================================**/

const { getAllDrawRequestsNew } = drawRequestsService;

const options: IManipulateReportsOptions = {
  displayOptions: [{ value: "monthly", label: "Monthly" }],
  rangeOptions: [
    { value: "this-month", label: "This Month" },
    { value: "last-month", label: "Last Month" },
    { value: "range", label: "Range" }
  ],
  searchOption: false
};

const filterOptions = [
  {
    value: "",
    label: "All"
  },
  {
    value: "Pending",
    label: "Pending"
  },
  {
    value: "Approved",
    label: "Approved"
  },
  {
    value: "Denied",
    label: "Denied"
  }
];
const filterApprovalStatusOptions = [
  {
    value: "",
    label: "All"
  },
  {
    value: "Initiator",
    label: "Initiator"
  },
  // {
  //   value: "Approver",
  //   label: "Approvers"
  // },
  // {
  //   value: "Expat officer",
  //   label: "Expat Officer"
  // },
  // {
  //   value: "Expat in charge",
  //   label: "Expat in Charge"
  // },
  {
    value: "Reviewers",
    label: "Reviewers"
  },
  {
    value: "Completed",
    label: "Completed"
  },
  {
    value: "Rejected",
    label: "Rejected"
  },
  {
    value: "Voided",
    label: "Voided"
  }
];

const paymentMethods = [
  {
    value: "",
    label: "All"
  },
  {
    value: "AchCheck",
    label: "ACH / Check Payment"
  },
  {
    value: "Wire",
    label: "Wire Payment"
  },
  {
    value: "manualCheck",
    label: "Manual Check"
  }
];

/*============================ END OF SECTION ============================*/

/**========================================================================
 *                           external methods here
 *========================================================================**/

/*============================ END OF SECTION ============================*/

let searchedDebounce: NodeJS.Timeout | null | undefined = null;

export const DrawRequestNew: FC<Props> = () => {
  /**========================================================================
   *                           Hooks here
   *========================================================================**/
  const navigate = useNavigate();
  const { user, isAccountant, isTreasury } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [filters, setFilters] = useState<IManipulateReportsFilters>({
    displayMode: "monthly",
    downloadType: "excel",
    rangeOptionValue: "this-month",
    searchValue: "",
    range: [moment().startOf("month"), moment().endOf("month")]
  });

  const [filterValue, setFilterValue] = useState<"approved" | "denied" | null | "">("");

  const [searchedValue, setSearchedValue] = useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");

  useEffect(() => {
    if (searchedDebounce) clearTimeout(searchedDebounce);
    searchedDebounce = setTimeout(() => {
      setSearchedValue(searchValue);
    }, 500);
  }, [searchValue]);

  const [filterApprovalStatusValue, setFilterApprovalStatusValue] = useState("");

  const [paymentTypeValue, setPaymentTypeValue] = useState<string | null>("");
  const [drawRequestsData, setDrawRequestsData] = useState<DrawRequestResp[]>([]);

  const [needToApproveOnly, setNeedToApproveOnly] = useState(false);

  const { isLoading: isDRLoading, isFetching } = useQuery(
    [
      "draw-requests",
      filterValue,
      isMoment(filters.range) ? filters.range.format("MM/DD/YYYY") : (filters.range as moment.Moment[])[0].format("MM/DD/YYYY"),
      isMoment(filters.range) ? filters.range.format("MM/DD/YYYY") : (filters.range as moment.Moment[])[1].format("MM/DD/YYYY"),
      paymentTypeValue,
      filterApprovalStatusValue,
      searchedValue,
      needToApproveOnly
    ],
    (context) =>
      getAllDrawRequestsNew({
        status: filterValue,
        startDate: context.queryKey[2] as string,
        endDate: context.queryKey[3] as string,
        paymentType: getPaymentTypesQuery,
        filterApprovalStatusValue,
        controlNumber: searchedValue,
        myApproval: needToApproveOnly
      }),
    {
      initialData: [],
      onSuccess: (data) => {
        setDrawRequestsData((value) => [...data]);
      }
    }
  );

  const tableData = useCallback(() => {
    return drawRequestsData;
  }, [drawRequestsData]);

  /*============================ END OF SECTION ============================*/

  /**========================================================================
   *                           Methods here
   *========================================================================**/

  const downloadAllCsv = async (isBulk: boolean = false) => {
    setIsLoading(true);
    try {
      const file = await drawRequestsService.getAllCsv(
        filterValue,
        isMoment(filters.range) ? filters.range.format("MM/DD/YYYY") : (filters.range as moment.Moment[])[0].format("MM/DD/YYYY"),
        isMoment(filters.range) ? filters.range.format("MM/DD/YYYY") : (filters.range as moment.Moment[])[1].format("MM/DD/YYYY"),
        getPaymentTypesQuery,
        filterApprovalStatusValue,
        isBulk
      );

      const fileName = (!isBulk ? "draw-request-csv" : "draw-request-bulk") + `${new Date().toUTCString()}`;

      const fileExtension = !isBulk ? "csv" : "zip";

      generateDownload(file, fileName, fileExtension);

      message.info("All documents downloaded successfully");
    } catch (error) {
      console.error(error);
      message.error("Ups: something was wrong downloading the documents, please try again later or contact the support");
    }
    setIsLoading(false);
  };

  const getPaymentTypesQuery = useMemo(() => {
    switch (paymentTypeValue) {
      case "AchCheck":
        return [3, 4];
      case "Wire":
        return [1, 2];
      case "manualCheck":
        return [5];
      default:
        return [];
    }
  }, [paymentTypeValue]);

  const onAction = (type: DrawRequestApprovalType, drawRequest?: DrawRequestResp) => {};

  // const setValue = (value: DrawRequestResponse[], id: number) => {};

  const onFiltersChange = (filters: IManipulateReportsFilters) => {
    setFilters(filters);
  };

  const onNeedToApproveOnly: CheckboxProps["onChange"] = (e) => {
    const value = e.target.checked;
    setNeedToApproveOnly(value);
  };
  /*============================ END OF SECTION ============================*/

  return (
    <Spin spinning={isLoading}>
      {/* /========================================================================  HTML here
    ========================================================================*/}
      <Row className={styles.mtMb15} style={{ justifyContent: "flex-end" }}>
        <Col>
          <Button icon={<PlusOutlined></PlusOutlined>} type="primary" onClick={() => navigate("new-draw-request")}>
            Create New Request
          </Button>
        </Col>
      </Row>

      <Row style={{ width: "100%" }} className={styles.mtMb15}>
        <Col span={24}>
          <Divider orientation="center">Payment Request List ({drawRequestsData.length})</Divider>
          <Row>
            <Col style={{ marginBottom: "25px" }}>
              <ManipulateReports
                options={options}
                filters={filters}
                onFiltersChange={onFiltersChange}
                onDownloadClick={() => console.log}
              />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Row style={{ marginBottom: "10px" }}>Filter:</Row>
              <Row>
                <Col style={{ marginRight: "20px" }}>
                  Admin Status:
                  <Select
                    onChange={setFilterValue}
                    style={{ width: 200, marginBottom: "1rem", marginLeft: "0.5rem" }}
                    options={filterOptions}
                    value={filterValue}
                  />
                </Col>
                <Col style={{ marginRight: "20px" }}>
                  Approval Status:
                  <Select
                    onChange={setFilterApprovalStatusValue}
                    style={{ width: 200, marginBottom: "1rem", marginLeft: "0.5rem" }}
                    options={filterApprovalStatusOptions}
                    value={filterApprovalStatusValue}
                  />
                </Col>
                <Col style={{ marginRight: "20px" }}>
                  Payment Type:
                  <Select
                    onChange={setPaymentTypeValue}
                    style={{ width: 200, marginBottom: "1rem", marginLeft: "0.5rem" }}
                    options={paymentMethods}
                    value={paymentTypeValue}
                  />
                </Col>
                <Col style={{ marginRight: "20px" }}>
                  Control Number:
                  <Input
                    onChange={(e) => setSearchValue(e.target.value)}
                    style={{ width: 200, marginBottom: "1rem", marginLeft: "0.5rem" }}
                    value={searchValue}
                  />
                </Col>
                <Col style={{ display: "flex" }}>
                  <div
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setNeedToApproveOnly((value) => !value);
                    }}
                  >
                    Show what I need to approve only:
                  </div>
                  <Checkbox
                    style={{ marginBottom: "1rem", marginLeft: "0.5rem" }}
                    checked={needToApproveOnly}
                    onChange={onNeedToApproveOnly}
                  />
                </Col>
              </Row>
            </Col>

            <Col span={24} style={{ display: "flex", justifyContent: "flex-end", marginBottom: 20 }}>
              {(isTreasury || isAccountant) && filterValue?.toLowerCase() === "approved" && (
                <>
                  <Button style={{ marginRight: 10 }} onClick={() => downloadAllCsv()} icon={<CloudDownloadOutlined />} type="primary">
                    Download CSV's
                  </Button>
                  <Button onClick={() => downloadAllCsv(true)} icon={<CloudDownloadOutlined />} type="primary">
                    Bulk PDF Download
                  </Button>
                </>
              )}
            </Col>
          </Row>

          <Row style={{ width: "100%" }}>
            <Col span={24}>
              {!isDRLoading && !isFetching && (
                <DrawRequestsTableNew
                  data={tableData()}
                  onAction={onAction}
                  setData={setDrawRequestsData}
                  loading={isDRLoading || isFetching}
                  hideApproveAll={true}
                />
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      {/*============================ END OF SECTION ============================*/}
    </Spin>
  );
};
